import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, HandHeart, Sparkles, Waves } from "lucide-react";
import { siteData, realImages } from "../mock";
import { FASCIA_SPECIALTIES } from "../data/specialties";
import { fasciaUrl } from "../data/urls";
import Reviews from "../components/Reviews";
import FAQ from "../components/FAQ";
import CTASection from "../components/CTASection";
import RelatedArticles from "../components/RelatedArticles";
import SEO from "../components/SEO";
import { PAGE_META } from "../data/pageMeta";

const benefits = [
  { icon: Waves, title: "Toucher doux", desc: "Une technique manuelle en douceur, sans manipulation forte." },
  { icon: HandHeart, title: "Soulagement rapide", desc: "Des résultats visibles dès la première séance." },
  { icon: Sparkles, title: "Corps léger", desc: "Retrouvez amplitude, fluidité et confort au quotidien." },
];

export default function Fasciatherapie() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO
        {...PAGE_META["/fasciathérapie-toulouse"]}
        keywords={["fasciathérapie Toulouse", "Tissual Balancing", "douleurs chroniques", "mal de dos", "Saint-Orens", "Quint-Fonsegrives"]}
      />
      <section className="container-tight py-16 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="chip mb-6">Méthode Tissual Balancing</span>
          <h1 className="font-serif text-5xl md:text-6xl font-medium text-[#1a1815] tracking-tight leading-[1.05]">
            Fasciathérapie à Toulouse
          </h1>
          <p className="mt-4 font-serif text-2xl text-[#4a4642] leading-snug">
            Soulagez vos douleurs en profondeur, retrouvez votre mobilité
          </p>
          <p className="mt-5 text-lg text-[#4a4642] leading-relaxed">
            Une approche douce et efficace pour libérer les tensions, réduire la douleur et améliorer votre bien-être durablement.
          </p>
          <div className="mt-8 flex gap-3 flex-wrap">
            <a
              href="https://www.resalib.fr/praticien/120570-jean-christophe-happillon-fasciatherapeute-quint-fonsegrives"
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
            >
              Réserver une séance <ArrowRight size={16} />
            </a>
            <a href={`tel:${siteData.practitioner.phone.replace(/\s/g, "")}`} className="btn-outline">
              {siteData.practitioner.phone}
            </a>
          </div>
        </div>
        <div className="rounded-[2rem] overflow-hidden aspect-[4/5]">
          <img src={realImages.fasciaSession} alt="Séance de fasciathérapie à Toulouse" className="w-full h-full object-cover" />
        </div>
      </section>

      <section className="container-tight py-16">
        <div className="grid md:grid-cols-3 gap-4">
          {benefits.map((b, i) => (
            <div key={i} className="bg-white rounded-3xl p-8 border border-[#e8e0d5]">
              <div className="w-12 h-12 rounded-2xl bg-[#f7f2ec] flex items-center justify-center mb-5">
                <b.icon size={22} className="text-[#d97757]" />
              </div>
              <h3 className="font-serif text-xl font-medium mb-2 text-[#1a1815]">{b.title}</h3>
              <p className="text-[#4a4642] text-sm leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#f0e4d5]/50 py-24">
        <div className="container-tight max-w-3xl">
          <span className="chip mb-4">Comprendre les fascias</span>
          <h2 className="font-serif text-4xl font-medium text-[#1a1815] leading-tight mb-6">
            Une approche douce, une action profonde
          </h2>
          <div className="space-y-4 text-[#4a4642] leading-relaxed">
            <p>Les fascias entourent tous vos organes, vos muscles et vos tendons. Ils sont la continuité de vos muscles, appelées les chaînes myofasciales.</p>
            <p>Lorsque vous êtes bloqué pour, par exemple, toucher vos pieds en vous penchant, il se peut que ce qui vous empêche vienne d'une adhérence sur l'une de ces chaînes.</p>
            <p>Mon travail : analyser votre posture, évaluer les mouvements que votre corps n'arrive plus à faire, puis pratiquer un soin doux qui amène les fascias à retrouver hydratation et glissement.</p>
          </div>
          <ul className="mt-8 space-y-3">
            {[
              "Analyse posturale complète",
              "Tests de mobilité spécifiques",
              "Soin manuel doux et ciblé",
              "Exercices personnalisés à la fin de séance",
            ].map((it, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-[#d97757] flex-shrink-0 mt-0.5" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <Reviews />
      <FAQ />

      {/* Specialty pages internal linking for SEO */}
      <section className="container-tight py-16">
        <div className="max-w-2xl mb-10">
          <span className="chip mb-4">Motifs de consultation</span>
          <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#2b2620] tracking-tight leading-tight">
            Ce que je traite en fasciathérapie
          </h2>
          <p className="mt-3 text-[#5c554c]">Chaque problématique fait l'objet d'un accompagnement dédié.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FASCIA_SPECIALTIES.map((s) => (
            <Link key={s.slug} to={fasciaUrl(s.slug)} className="group bg-white rounded-2xl border border-[#eee5d6] p-6 hover:border-[#c76d4b]/40 hover:shadow-md transition-all">
              <h3 className="font-serif text-lg font-medium text-[#2b2620] group-hover:text-[#c76d4b] transition-colors">
                {s.keyword.charAt(0).toUpperCase() + s.keyword.slice(1)}
              </h3>
              <p className="text-sm text-[#5c554c] mt-2 line-clamp-2 leading-relaxed">{s.intro.substring(0, 130)}…</p>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#c76d4b]">
                Découvrir <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <RelatedArticles
        category="Fasciathérapie"
        title="Approfondissez le sujet"
        subtitle="Articles récents sur les fascias, la méthode Tissual Balancing et les techniques de soulagement des tensions."
      />
      <CTASection />
    </>
  );
}
